/**
 * Security Settings Page
 *
 * Manage security settings, password, and two-factor authentication.
 */

import { useState } from 'react';
import { Logger, LogCategory } from '@/utils/logger';
import { AppLayout, PageHeader } from '@/components/layout';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { supabase } from '@/lib/supabase';
import { useUser } from '@/stores/authStore';
import { isValidPassword } from '@/utils';
import {
  Shield,
  Lock,
  Check,
  X,
  Monitor,
} from 'lucide-react';

export function SecuritySettingsPage() {
  const user = useUser();
  const { success: showSuccess, error: showError } = useToast();

  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);

  const handlePasswordChange = async () => {
    Logger.info(LogCategory.AUTH, 'Password change initiated', 'SecuritySettingsPage');

    if (!currentPassword) {
      showError('Error', 'Please enter your current password.');
      return;
    }

    const validation = isValidPassword(newPassword);
    if (!validation.valid) {
      setPasswordErrors(validation.errors);
      return;
    }

    if (newPassword !== confirmPassword) {
      showError('Error', 'New passwords do not match.');
      return;
    }

    try {
      // Verify current password by re-authenticating
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) throw new Error('Not authenticated');

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });

      if (signInError) {
        showError('Error', 'Current password is incorrect.');
        return;
      }

      // Update to new password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) throw updateError;

      Logger.info(LogCategory.AUTH, 'Password changed successfully', 'SecuritySettingsPage');
      showSuccess('Password changed', 'Your password has been updated successfully.');
      setIsPasswordModalOpen(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordErrors([]);
    } catch (err: any) {
      Logger.error(LogCategory.AUTH, 'Password change failed', 'SecuritySettingsPage', undefined, err);
      showError('Error', err?.message || 'Failed to change password.');
    }
  };

  const passwordStrength = (() => {
    if (!newPassword) return null;
    const validation = isValidPassword(newPassword);
    const score = 4 - validation.errors.length;
    if (score <= 1) return { label: 'Weak', color: 'danger' };
    if (score === 2) return { label: 'Fair', color: 'warning' };
    if (score === 3) return { label: 'Good', color: 'info' };
    return { label: 'Strong', color: 'success' };
  })();

  return (
    <AppLayout>
      <PageHeader
        title="Security"
        description="Manage your account security settings"
      />

      <div className="max-w-3xl space-y-6">
        {/* Security Overview */}
        <Card variant="elevated" className="bg-gradient-to-r from-slate-700 to-slate-800">
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-200 text-sm font-medium">Security Status</p>
                <p className="text-2xl font-bold text-white mt-1">Basic</p>
                <p className="text-slate-300 text-sm mt-2">
                  Additional security features coming soon
                </p>
              </div>
              <div className="h-16 w-16 bg-white/10 rounded-full flex items-center justify-center">
                <Shield className="h-8 w-8 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Password */}
        <Card>
          <CardHeader
            title="Password"
            description="Change your account password"
          />
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Lock className="h-5 w-5 text-slate-500" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Password</p>
                  <p className="text-sm text-slate-500">
                    Last changed 30 days ago
                  </p>
                </div>
              </div>
              <Button variant="outline" onClick={() => setIsPasswordModalOpen(true)}>
                Change Password
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Two-Factor Authentication */}
        <Card>
          <CardHeader
            title="Two-Factor Authentication"
          />
          <CardContent>
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Shield className="h-12 w-12 text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-1">Coming Soon</h3>
              <p className="text-sm text-slate-500">
                Two-factor authentication will be available in a future update.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Active Sessions */}
        <Card>
          <CardHeader
            title="Active Sessions"
          />
          <CardContent>
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Monitor className="h-12 w-12 text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-1">Coming Soon</h3>
              <p className="text-sm text-slate-500">
                Session management will be available in a future update.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Security Recommendations */}
        <Card>
          <CardHeader
            title="Security Checklist"
            description="Recommended security measures"
          />
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className={`h-6 w-6 rounded-full flex items-center justify-center ${
                  true ? 'bg-success-100' : 'bg-slate-100'
                }`}>
                  <Check className="h-4 w-4 text-success-600" />
                </div>
                <span className="text-sm text-slate-700">Strong password set</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="h-6 w-6 rounded-full flex items-center justify-center bg-slate-100">
                  <X className="h-4 w-4 text-slate-400" />
                </div>
                <span className="text-sm text-slate-700">Two-factor authentication (coming soon)</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="h-6 w-6 rounded-full flex items-center justify-center bg-success-100">
                  <Check className="h-4 w-4 text-success-600" />
                </div>
                <span className="text-sm text-slate-700">Email verified</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="h-6 w-6 rounded-full flex items-center justify-center bg-success-100">
                  <Check className="h-4 w-4 text-success-600" />
                </div>
                <span className="text-sm text-slate-700">Recent login activity reviewed</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Change Password Modal */}
      <Modal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        title="Change Password"
        size="sm"
      >
        <div className="space-y-4">
          <Input
            type="password"
            label="Current Password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
          <div>
            <Input
              type="password"
              label="New Password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setPasswordErrors([]);
              }}
            />
            {passwordStrength && (
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      passwordStrength.color === 'danger' ? 'bg-danger-500 w-1/4' :
                      passwordStrength.color === 'warning' ? 'bg-warning-500 w-2/4' :
                      passwordStrength.color === 'info' ? 'bg-info-500 w-3/4' :
                      'bg-success-500 w-full'
                    }`}
                  />
                </div>
                <span className={`text-xs font-medium text-${passwordStrength.color}-600`}>
                  {passwordStrength.label}
                </span>
              </div>
            )}
            {passwordErrors.length > 0 && (
              <ul className="mt-2 space-y-1">
                {passwordErrors.map((error, i) => (
                  <li key={i} className="text-xs text-danger-600 flex items-center gap-1">
                    <X className="h-3 w-3" />
                    {error}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <Input
            type="password"
            label="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setIsPasswordModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handlePasswordChange}>
              Change Password
            </Button>
          </div>
        </div>
      </Modal>

    </AppLayout>
  );
}
